import { DonorMascot } from '../DonorMascot'
import type { MascotConfig } from '../mascot-catalog'
import type { MascotGameState, Room } from './mascot-game-types'
import { mascotMood, projectNeeds } from './mascot-game-view'
import { MascotVitals } from './MascotVitals'
import styles from './MascotScene.module.css'
import { useEffect, useState, type CSSProperties } from 'react'
import { mascotSceneColor } from '../mascot-colors'
import { JungleDrawing } from './JungleDrawing'
import { FarmDrawing } from './FarmDrawing'

export function MascotScene({
  mascot,
  state,
  room,
  now,
  reaction,
  petReaction = 0,
  careEffect,
  onPet,
  onRoom = () => {},
  disabled,
}: {
  mascot: MascotConfig
  state: MascotGameState
  room: Room
  now: number
  reaction: string
  petReaction?: number
  careEffect: string
  onPet: () => void
  onRoom?: (room: Room) => void
  disabled: boolean
}) {
  const sleeping = state.sleeping
  const [transition, setTransition] = useState<{
    sleeping: boolean
    phase: 'idle' | 'sleep-stretch' | 'settling' | 'wake-stretch' | 'wake-rest' | 'yawn'
  }>({ sleeping, phase: 'idle' })
  if (transition.sleeping !== sleeping) {
    setTransition({ sleeping, phase: sleeping ? 'sleep-stretch' : 'wake-stretch' })
  }
  const phase = transition.phase
  useEffect(() => {
    if (phase === 'idle') return
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    const next = {
      'sleep-stretch': ['settling', 1200],
      settling: ['idle', 900],
      'wake-stretch': ['wake-rest', 1800],
      'wake-rest': ['yawn', 400],
      yawn: ['idle', 1600],
    } as const
    const [nextPhase, delay] = next[phase]
    const timer = window.setTimeout(
      () => {
        setTransition((current) => ({
          ...current,
          phase: reducedMotion ? 'idle' : nextPhase,
        }))
      },
      reducedMotion ? 0 : delay
    )
    return () => window.clearTimeout(timer)
  }, [phase, sleeping])
  const resting = sleeping && phase !== 'sleep-stretch'
  const needs = projectNeeds(state, now)
  const sceneColor = mascotSceneColor(mascot)
  const customScene =
    mascot.sceneColor || (mascot.backdrop && mascot.backdrop !== 'classic')
  return (
    <div
      className={`${styles.scene} ${sleeping ? styles.night : ''}`}
      data-room={room}
      data-habitat={
        mascot.species === 'jaguar' && mascot.backdrop !== 'farm'
          ? 'jungle'
          : mascot.backdrop === 'farm'
            ? 'farm'
            : 'home'
      }
      data-decor={state.decor}
      data-sleeping={sleeping}
      data-resting={resting}
      style={
        customScene
          ? ({
              '--wall': sceneColor,
            } as CSSProperties)
          : undefined
      }
    >
      {mascot.species === 'jaguar' && mascot.backdrop !== 'farm' ? (
        <JungleDrawing
          room={room}
          sleeping={sleeping}
          growing={state.garden !== null}
          decor={state.decor}
          tint={mascot.sceneColor}
        />
      ) : mascot.backdrop === 'farm' ? (
        <FarmDrawing
          room={room}
          sleeping={sleeping}
          growing={state.garden !== null}
          decor={state.decor}
          tint={mascot.sceneColor}
          pasture={mascot.species === 'sheep'}
        />
      ) : (
        <RoomDrawing
          room={room}
          sleeping={sleeping}
          growing={state.garden !== null}
          backdrop={mascot.backdrop}
        />
      )}
      <MascotVitals
        state={state}
        now={now}
        onRoom={onRoom}
        carnivore={mascot.species === 'jaguar'}
        herbivore={mascot.species === 'sheep'}
      />
      <div className={styles.roomPlaque}>
        <span />{' '}
        {sleeping
          ? 'Modo sonequinha'
          : mascot.species === 'jaguar' && mascot.backdrop !== 'farm'
            ? 'Um refúgio na mata'
            : mascot.backdrop === 'farm'
              ? 'Um cantinho de vida no campo'
              : 'Feito de pequenos cuidados'}
      </div>
      <p className={styles.speech} key={`${reaction || room}-${petReaction}`}>
        {reaction || mascotMood(state, now)}
      </p>
      <button
        className={styles.pet}
        data-mascot-interaction
        onClick={onPet}
        disabled={disabled}
        aria-label={
          sleeping
            ? `Fazer carinho em ${mascot.name} dormindo`
            : `Fazer carinho em ${mascot.name}`
        }
      >
        <span className={styles.petShadow} />
        <span className={styles.petVisual}>
          <span className={styles.awakePet} aria-hidden={resting}>
            <DonorMascot
              config={mascot}
              showBackdrop={false}
              needs={needs}
              motion={
                phase === 'sleep-stretch' || phase === 'settling'
                  ? 'sleep-stretch'
                  : phase === 'wake-stretch'
                    ? phase
                    : undefined
              }
              expression={
                phase === 'yawn'
                  ? 'yawning'
                  : reaction && !careEffect
                    ? 'tickled'
                    : 'happy'
              }
            />
          </span>
          <span className={styles.sleepingPet} aria-hidden={!resting}>
            <DonorMascot
              config={mascot}
              showBackdrop={false}
              needs={needs}
              expression="sleepy"
            />
          </span>
        </span>
        {resting && phase === 'idle' && (
          <span className={styles.zzz} aria-hidden>
            <i>z</i>
            <i>z</i>
            <i>Z</i>
          </span>
        )}
        {reaction && (
          <span className={styles.hearts} key={petReaction} aria-hidden>
            ♡ ✦ ♡
          </span>
        )}
        {reaction && careEffect && (
          <span
            className={styles.careEffect}
            key={`${state.stats.feeds}-${state.stats.washes}-${state.updated_at}`}
            aria-hidden
          >
            {careEffect}
          </span>
        )}
      </button>
      <span className={styles.petName}>{mascot.name}</span>
    </div>
  )
}

function RoomDrawing({
  room,
  sleeping,
  growing,
  backdrop,
}: {
  room: Room
  sleeping: boolean
  growing: boolean
  backdrop: MascotConfig['backdrop']
}) {
  const garden = room === 'garden'
  return (
    <svg
      className={styles.drawing}
      viewBox="0 0 960 600"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      <rect width="960" height="600" className={styles.wall} />
      {garden ? (
        <>
          <circle cx="750" cy="120" r="50" fill="#ffe299" />
          <g fill="#fff" opacity=".65">
            <ellipse cx="200" cy="128" rx="105" ry="30" />
            <ellipse cx="275" cy="118" rx="55" ry="40" />
            <ellipse cx="640" cy="200" rx="95" ry="22" />
          </g>
          <path d="M0 420Q220 290 500 410T960 350V600H0Z" fill="#b2c6a0" />
          <path d="M0 460Q250 380 510 470T960 410V600H0Z" fill="#8bab80" />
          <path
            d="M730 420V264"
            stroke="#91694c"
            strokeWidth="24"
            strokeLinecap="round"
          />
          <g fill={growing ? '#668b5c' : '#91ae75'}>
            <circle cx="700" cy="260" r="65" />
            <circle cx="770" cy="260" r="72" />
            <circle cx="735" cy="200" r="62" />
          </g>
          {growing && (
            <g fill="#e58d70">
              <circle cx="700" cy="244" r="11" />
              <circle cx="765" cy="215" r="11" />
              <circle cx="781" cy="289" r="11" />
            </g>
          )}
          <g fill="#f5dda3">
            <circle cx="120" cy="478" r="6" />
            <circle cx="815" cy="505" r="6" />
            <circle cx="202" cy="507" r="6" />
          </g>
        </>
      ) : (
        <>
          <path d="M0 432H960V600H0Z" className={styles.floor} />
          <path d="M0 432H960" stroke="#fff" strokeOpacity=".5" strokeWidth="12" />
          <path
            d="M70 600 265 440m0 160 115-160m350 160L615 440m270 160L680 440"
            stroke="#000"
            strokeOpacity=".035"
            strokeWidth="2"
          />
          <rect x="105" y="95" width="185" height="213" rx="82" fill="#fffaf0" />
          <rect
            x="118"
            y="108"
            width="159"
            height="186"
            rx="73"
            fill={sleeping ? '#344b65' : '#bad9df'}
          />
          <circle cx="226" cy="155" r="25" fill={sleeping ? '#fff0c0' : '#ffe7a0'} />
          <path
            d="M125 251q53-60 146-4v47H125Z"
            fill={sleeping ? '#466079' : '#97bc9a'}
          />
          <path d="M198 113v183M116 223h164" stroke="#fffaf0" strokeWidth="10" />
          <rect x="90" y="301" width="216" height="12" rx="5" fill="#f9f0dc" />
          <ellipse cx="478" cy="508" rx="216" ry="45" className={styles.rug} />
          {room === 'kitchen' ? (
            <>
              <rect x="690" y="247" width="170" height="193" rx="14" fill="#f6e6c8" />
              <rect x="678" y="230" width="194" height="24" rx="8" fill="#a77959" />
              <path d="M774 260v164" stroke="#d8c4a0" strokeWidth="3" />
              <circle cx="755" cy="338" r="4" fill="#a77959" />
              <circle cx="794" cy="338" r="4" fill="#a77959" />
              <path d="M721 195h100q-8 40-50 40t-50-40" fill="#d47d60" />
              <circle cx="751" cy="189" r="16" fill="#da7d65" />
              <circle cx="785" cy="184" r="19" fill="#e7bb61" />
              <path
                d="M770 174q2-18 14-18"
                stroke="#6c915c"
                strokeWidth="6"
                strokeLinecap="round"
              />
            </>
          ) : room === 'bath' ? (
            <>
              <path d="M658 355h238l-25 84H685Z" fill="#fbfcf6" />
              <rect x="646" y="340" width="260" height="26" rx="13" fill="#e5eee9" />
              <path
                d="M850 338V243q0-26-28-26h-18"
                fill="none"
                stroke="#9eb5b1"
                strokeWidth="12"
                strokeLinecap="round"
              />
              <g fill="#fff" opacity=".8">
                <circle cx="726" cy="334" r="19" />
                <circle cx="754" cy="313" r="28" />
                <circle cx="792" cy="328" r="23" />
              </g>
              <rect x="717" y="90" width="116" height="144" rx="55" fill="#f9f3df" />
              <rect x="728" y="101" width="94" height="122" rx="44" fill="#bfdbd9" />
            </>
          ) : room === 'bedroom' ? (
            <>
              <rect x="674" y="283" width="203" height="159" rx="35" fill="#a68069" />
              <rect x="661" y="335" width="230" height="100" rx="22" fill="#eddbb8" />
              <rect x="681" y="304" width="112" height="46" rx="20" fill="#fff7e7" />
              <path d="M809 342h74v85h-74" fill="#93ae97" />
              <path
                d="m821 357 15 12-15 12 15 12-15 12"
                stroke="#f2e3bc"
                fill="none"
                strokeWidth="5"
              />
            </>
          ) : (
            <>
              <rect x="702" y="102" width="110" height="130" rx="8" fill="#ba8a64" />
              <rect x="712" y="112" width="90" height="110" rx="3" fill="#fff0cd" />
              <path d="M758 190c-43-30-10-57 0-33 17-24 40 10 0 33" fill="#d68f70" />
              <path d="M711 407h102l-17 51h-68Z" fill="#dba887" />
              <path
                d="M762 408V320m0 41q-85-4-52-58 51 2 52 58m0 11q72-2 48-52-45 5-48 52"
                fill="#779c76"
                stroke="#779c76"
                strokeWidth="6"
              />
            </>
          )}
        </>
      )}
      {backdrop === 'clouds' && (
        <g fill="#fff" opacity=".45">
          <ellipse cx="450" cy="90" rx="80" ry="20" />
          <circle cx="450" cy="70" r="32" />
          <ellipse cx="615" cy="155" rx="62" ry="17" />
        </g>
      )}
      {backdrop === 'sunset' && (
        <circle cx="590" cy="105" r="42" fill="#ffe8a9" opacity=".75" />
      )}
      {(sleeping || backdrop === 'stars') && (
        <g fill="#fff4c9">
          <path d="m414 75 4 10 11 1-9 7 3 11-9-6-9 6 3-11-9-7 11-1Z" />
          <circle cx="580" cy="120" r="3" />
          <circle cx="340" cy="180" r="3" />
        </g>
      )}
    </svg>
  )
}
